import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import { dashboardService, DashboardStatsResponse } from '../services/dashboardService';
import { classService } from '../services/classService';
import { schoolService } from '../services/schoolService';
import {
  siteStatisticsService,
  DailyAnswerCount,
  SchoolParticipationBucket,
  CompletionStatusPie,
  GradeDistribution,
  QuestionnaireWithAnswers,
} from '../services/siteStatisticsService';
import { School } from '../types';
import { SchoolIcon, FileTextIcon, CheckCircleIcon } from '../components/Icons';

const COMPLETION_COLORS: Record<string, string> = {
  '已完成': '#22c55e',
  '进行中': '#eab308',
  '未开始': '#9ca3af',
};

// 年级配色 — 不同年级一眼区分（避开 completion 那 3 色）
const GRADE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6'];

// 学校参与三段位配色
const BUCKET_COLORS: Record<string, string> = {
  '热门': '#dc2626',
  '中等': '#f59e0b',
  '冷门': '#6b7280',
};

type TimeWindow = '7d' | '30d' | '90d' | 'year';

const SiteStatisticsPage: React.FC = () => {
  const navigate = useNavigate();

  // 顶部 3 张卡 + 学年列表
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [academicYears, setAcademicYears] = useState<string[]>([]);

  // 筛选条
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | undefined>(undefined);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | undefined>(undefined);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('30d');

  // 下拉数据
  const [schools, setSchools] = useState<School[]>([]);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireWithAnswers[]>([]);

  // 图表数据
  const [trend, setTrend] = useState<DailyAnswerCount[]>([]);
  const [schoolBuckets, setSchoolBuckets] = useState<SchoolParticipationBucket | null>(null);
  const [completion, setCompletion] = useState<CompletionStatusPie | null>(null);
  const [grade, setGrade] = useState<GradeDistribution | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 首次加载
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [s, ys, allSchools] = await Promise.all([
          dashboardService.getDashboardStats(),
          classService.getAllAcademicYears(),
          schoolService.getAllSchoolsSimple(),
        ]);
        setStats(s);
        setAcademicYears(ys);
        setSchools(allSchools);
        if (ys.length > 0) setSelectedYear(ys[0]);
      } catch (e) {
        console.error(e);
        setError('加载统计数据失败');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 选了学校 → 拉该学校的问卷下拉
  useEffect(() => {
    setSelectedQuestionnaireId(undefined);
    if (selectedSchoolId === undefined) {
      setQuestionnaires([]);
      return;
    }
    let cancelled = false;
    siteStatisticsService.getQuestionnairesBySchool(selectedSchoolId)
      .then(qs => { if (!cancelled) setQuestionnaires(qs); })
      .catch(e => { console.error(e); if (!cancelled) setQuestionnaires([]); });
    return () => { cancelled = true; };
  }, [selectedSchoolId]);

  // 计算趋势图日期范围
  const dateRange = useMemo(() => {
    const today = new Date();
    const to = new Date(today);
    to.setDate(to.getDate() + 1); // 闭开 [from, to)，包含今天
    let from = new Date(today);
    if (timeWindow === '7d') from.setDate(today.getDate() - 6);
    else if (timeWindow === '30d') from.setDate(today.getDate() - 29);
    else if (timeWindow === '90d') from.setDate(today.getDate() - 89);
    else if (timeWindow === 'year') {
      // 学年：用 selectedYear 的 9/1 → 次年 8/31，or 当前学年
      const year = selectedYear ? parseInt(selectedYear, 10) : today.getFullYear();
      from = new Date(year, 8, 1); // Sept 1
      to.setFullYear(year + 1, 7, 31); // Aug 31 next year
      // 但不超过今天
      const todayPlus1 = new Date(today);
      todayPlus1.setDate(today.getDate() + 1);
      if (to > todayPlus1) to.setTime(todayPlus1.getTime());
    }
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(from), to: fmt(to) };
  }, [timeWindow, selectedYear]);

  // 拉趋势图数据 — 跟着筛选条 + 时间窗变化
  const fetchTrend = useCallback(async () => {
    try {
      const data = await siteStatisticsService.getDailyAnswers(
        dateRange.from, dateRange.to,
        selectedSchoolId, selectedQuestionnaireId
      );
      setTrend(data);
    } catch (e) {
      console.error('Failed to fetch trend:', e);
      setTrend([]);
    }
  }, [dateRange.from, dateRange.to, selectedSchoolId, selectedQuestionnaireId]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  // 未选学校 → 学校参与三段位
  useEffect(() => {
    if (selectedSchoolId !== undefined) {
      setSchoolBuckets(null);
      return;
    }
    let cancelled = false;
    siteStatisticsService.getSchoolParticipation(selectedYear || undefined)
      .then(b => { if (!cancelled) setSchoolBuckets(b); })
      .catch(e => { console.error(e); if (!cancelled) setSchoolBuckets(null); });
    return () => { cancelled = true; };
  }, [selectedSchoolId, selectedYear]);

  // 选了 学校 + 问卷 → 完成状态 + 年级分布
  useEffect(() => {
    if (selectedSchoolId === undefined || selectedQuestionnaireId === undefined) {
      setCompletion(null);
      setGrade(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      siteStatisticsService.getCompletionStatus(selectedQuestionnaireId, selectedSchoolId),
      siteStatisticsService.getGradeDistribution(selectedQuestionnaireId, selectedSchoolId),
    ]).then(([c, g]) => {
      if (!cancelled) { setCompletion(c); setGrade(g); }
    }).catch(e => {
      console.error(e);
      if (!cancelled) { setCompletion(null); setGrade(null); }
    });
    return () => { cancelled = true; };
  }, [selectedSchoolId, selectedQuestionnaireId]);

  const renderLabel = ({ name, percent }: { name?: string; percent?: number }) =>
    `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-on-surface-variant font-body">加载统计数据...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 shadow-soft rounded-2xl p-6 text-center">
          <p className="text-red-600 mb-4 font-body">{error}</p>
        </div>
      </div>
    );
  }
  if (!stats) return null;

  // 趋势图数据准备
  const trendData = trend.map(t => ({ date: t.date.slice(5), count: t.count })); // MM-DD

  // 完成状态饼数据
  const completionData = completion
    ? [
        { name: '已完成', value: completion.completed },
        { name: '进行中', value: completion.inProgress },
        { name: '未开始', value: completion.pending },
      ].filter(d => d.value > 0)
    : [];

  // 年级分布饼数据
  const gradeData = (grade?.slices ?? []).map(s => ({ name: s.grade, value: s.count }));

  // 学校参与三段位 — 转成 stacked bar 数据：每个 bucket 一行，按学校列出 top 5
  const bucketBarData = schoolBuckets
    ? [
        ...schoolBuckets.hot.slice(0, 5).map(s => ({ name: s.schoolName, count: s.answerCount, bucket: '热门' })),
        ...schoolBuckets.medium.slice(0, 5).map(s => ({ name: s.schoolName, count: s.answerCount, bucket: '中等' })),
        ...schoolBuckets.cold.slice(0, 5).map(s => ({ name: s.schoolName, count: s.answerCount, bucket: '冷门' })),
      ]
    : [];

  const showFilteredPies = selectedSchoolId !== undefined && selectedQuestionnaireId !== undefined;

  return (
    <div className="p-8">
      {/* Breadcrumb + Header + 筛选条 */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 text-sm text-on-surface-variant font-label mb-2">
          <button onClick={() => navigate('/dashboard')} className="hover:text-primary-600">仪表盘</button>
          <span>/</span>
          <span className="text-on-surface">统计分析</span>
        </div>
        <h1 className="text-2xl font-bold text-on-surface font-headline">统计分析</h1>
        <p className="text-sm text-on-surface-variant font-body mt-1">
          查看系统整体运营数据和问卷完成情况
        </p>
      </div>

      {/* 筛选条 */}
      <div className="mb-6 bg-surface-container-lowest rounded-2xl shadow-soft p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-on-surface-variant font-label">学年：</span>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            className="text-sm bg-surface-container-high rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-200 font-body"
          >
            {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-on-surface-variant font-label">学校：</span>
          <select
            value={selectedSchoolId ?? ''}
            onChange={e => setSelectedSchoolId(e.target.value ? Number(e.target.value) : undefined)}
            className="text-sm bg-surface-container-high rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-200 font-body min-w-[160px]"
          >
            <option value="">全部学校</option>
            {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-on-surface-variant font-label">问卷：</span>
          <select
            value={selectedQuestionnaireId ?? ''}
            onChange={e => setSelectedQuestionnaireId(e.target.value ? Number(e.target.value) : undefined)}
            disabled={selectedSchoolId === undefined}
            className="text-sm bg-surface-container-high rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-200 font-body min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {selectedSchoolId === undefined ? '请先选择学校' : '全部问卷'}
            </option>
            {questionnaires.map(q => (
              <option key={q.questionnaireId} value={q.questionnaireId}>
                {q.title}（{q.answerCount} 份答卷）
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 顶部 3 卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <StatCard icon={<SchoolIcon size={24} />} value={stats.totalSchools} label="总学校数" iconBg="bg-primary-100" iconColor="text-primary-600" />
        <StatCard icon={<FileTextIcon size={24} />} value={stats.totalQuestionnaires} label="问卷总数" iconBg="bg-blue-100" iconColor="text-blue-600" />
        <StatCard icon={<CheckCircleIcon size={24} />} value={stats.totalPublished} label="已发布问卷" iconBg="bg-green-100" iconColor="text-green-600" />
      </div>

      {/* 趋势图（永远显示） */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold text-on-surface font-headline">每日答卷量趋势</h2>
            <p className="text-xs text-on-surface-variant font-body mt-1">
              {dateRange.from} 至 {dateRange.to}
              {selectedSchoolId !== undefined && schools.find(s => s.id === selectedSchoolId)?.name
                ? `  ·  ${schools.find(s => s.id === selectedSchoolId)?.name}`
                : '  ·  全平台'}
              {selectedQuestionnaireId !== undefined
                ? `  ·  ${questionnaires.find(q => q.questionnaireId === selectedQuestionnaireId)?.title}`
                : ''}
            </p>
          </div>
          <div className="flex items-center gap-1 bg-surface-container-high rounded-full p-1">
            {(['7d', '30d', '90d', 'year'] as TimeWindow[]).map(w => (
              <button
                key={w}
                onClick={() => setTimeWindow(w)}
                className={`text-xs px-3 py-1 rounded-full font-label transition-colors ${
                  timeWindow === w
                    ? 'bg-primary-600 text-white'
                    : 'text-on-surface-variant hover:bg-surface-container-highest'
                }`}
              >
                {w === '7d' ? '近 7 天' : w === '30d' ? '近 30 天' : w === '90d' ? '近 90 天' : '本学年'}
              </button>
            ))}
          </div>
        </div>
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v: any) => [`${v} 份`, '答卷']} />
              <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#trendFill)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[280px] text-on-surface-variant"><p className="font-body">暂无数据</p></div>
        )}
      </div>

      {/* 学校参与（未选学校时） / 完成 + 年级（选了学校 + 问卷时） */}
      {!showFilteredPies && schoolBuckets && (
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-on-surface font-headline">学校参与度</h2>
            <p className="text-xs text-on-surface-variant font-body mt-1">
              按答卷量排名分为<span className="text-red-600 mx-1">热门</span>/
              <span className="text-amber-600 mx-1">中等</span>/
              <span className="text-gray-500 mx-1">冷门</span> 三段（各段 Top 5）
            </p>
          </div>
          {bucketBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(220, bucketBarData.length * 32)}>
              <BarChart data={bucketBarData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(v: any, _name: any, props: any) => [`${v} 份`, props?.payload?.bucket || '答卷']} />
                <Bar dataKey="count">
                  {bucketBarData.map((entry, idx) => (
                    <Cell key={idx} fill={BUCKET_COLORS[entry.bucket] || '#9ca3af'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-on-surface-variant"><p className="font-body">暂无数据</p></div>
          )}
        </div>
      )}

      {showFilteredPies && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* 完成状态饼 */}
          <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
            <h2 className="text-lg font-semibold text-on-surface font-headline mb-4">问卷完成状态</h2>
            {completionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={completionData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={renderLabel}>
                    {completionData.map((d, idx) => (
                      <Cell key={idx} fill={COMPLETION_COLORS[d.name] || '#9ca3af'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`${v} 人`, '']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-on-surface-variant"><p className="font-body">暂无数据</p></div>
            )}
          </div>

          {/* 年级分布饼 */}
          <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
            <h2 className="text-lg font-semibold text-on-surface font-headline mb-4">答卷年级分布</h2>
            {gradeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={gradeData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={renderLabel}>
                    {gradeData.map((_d, idx) => (
                      <Cell key={idx} fill={GRADE_COLORS[idx % GRADE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`${v} 份`, '']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-on-surface-variant"><p className="font-body">暂无数据</p></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  value: number;
  label: string;
  iconBg: string;
  iconColor: string;
}> = ({ icon, value, label, iconBg, iconColor }) => (
  <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-soft hover:shadow-soft-md transition-shadow">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center ${iconColor}`}>
        {icon}
      </div>
    </div>
    <p className="text-3xl font-bold text-on-surface font-headline">{value}</p>
    <p className="text-sm text-on-surface-variant font-label mt-1">{label}</p>
  </div>
);

export default SiteStatisticsPage;
